/*
 * My Leads page body (PROD-9977): header, tiles, My shift today, per-level sections, help.
 * Presentational only; MyLeadsPage owns loading and refresh.
 */
import React, { memo } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Chip, LABEL_CLASS } from '../ui';
import StackTd from '../StackTable';
import { fmtTime } from '../session_allocation/allocation';
import { stackClasses } from '../stack_table';
import LeadsSelect from './LeadsSelect';
import RefreshCountdown from './RefreshCountdown';
import {
  DEFAULT_PERIOD,
  LEVEL_TONE,
  PERIODS,
  dayHeading,
  daysToPayCell,
  fmtDay,
  fmtDayTime,
  groupCountLabel,
  groupRowsByIstDate,
  phoneDayHeading,
  helpBullets,
  hourRowText,
  leadStatusCell,
  lastTouchCell,
  nowWithLabel,
  periodLabel,
  planLabel,
  sectionCount,
  sectionMeta,
  sectionsFor,
  shiftView,
  signalCell,
  refreshDueAt,
  tilesFor,
  touchBeforeLabel,
  trialStatusCell,
  updatedLabel,
  viaLabel
} from './my_leads';
import type { Cell, CellTone, GroupNoun, HeadingVerb, SectionId } from './my_leads';
import type { LeadsPeriod, MyLeadsData } from './types';
import type { RepLevel } from '../../sales_team/api';

const ST = stackClasses('md');
// Same table look as the Advisors and Sales users tabs.
// 20px card gutter on the outer columns, matching the panel header and shift body.
const EDGE = 'md:first:pl-5 md:last:pr-5';
const TH = `px-3.5 py-2 text-left ${EDGE} ${LABEL_CLASS}`;
// Body cells: the rowSpan date cell is first only on a day's first row, so no first-edge pad.
const TD =
  'px-3.5 py-2.5 text-left align-top text-[13px] font-normal text-content-primary md:last:pr-5';
const NUM = 'whitespace-nowrap tabular-nums';
// border-x-0/b-0 undo common.css's all-side tr border (as the Tasks list does).
const ROW = `border-x-0 border-b-0 border-t border-line-subtle text-[12.5px] hover:bg-surface-sunken max-md:border max-md:border-line-subtle ${ST.row}`;
const CARD = 'rounded-[10px] border border-line-subtle bg-surface-card';

const TONE_CLASS: Record<CellTone, string> = {
  plain: '',
  muted: 'text-content-muted',
  success: 'font-semibold text-state-success-text',
  warning: 'font-semibold text-state-warning-text',
  error: 'font-bold text-state-error-text',
  info: 'text-state-info-text'
};

function ToneText({ cell }: { cell: Cell }) {
  return <span className={TONE_CLASS[cell.tone]}>{cell.text}</span>;
}

function StudentLink({ uuid, name }: { uuid: string; name: string }) {
  return (
    <Link
      to={`/studentprofile?id=${encodeURIComponent(uuid)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="break-words font-semibold text-content-link hover:underline"
    >
      {name || 'Unnamed student'}
    </Link>
  );
}

// Card with a tinted title strip, as the Session allocation groups.
function Panel({
  testId,
  title,
  sub,
  aside,
  asideTestId,
  children
}: {
  testId: string;
  title: string;
  sub?: ReactNode;
  aside?: ReactNode;
  asideTestId?: string;
  children: ReactNode;
}) {
  return (
    <section aria-label={title} data-testid={testId} className={`overflow-hidden ${CARD}`}>
      <div className="flex flex-col gap-0.5 border-b border-line-subtle bg-surface-row-muted px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
          <h3 className="m-0 text-[13px] font-bold text-content-primary">{title}</h3>
          {aside ? (
            <span
              data-testid={asideTestId}
              className="text-xs tabular-nums text-content-secondary max-sm:w-full sm:ml-auto"
            >
              {aside}
            </span>
          ) : null}
        </div>
        {sub ? <p className="m-0 text-xs text-content-muted">{sub}</p> : null}
      </div>
      {children}
    </section>
  );
}

interface Col {
  label: string;
  // Share of the table width; table-fixed would otherwise split it evenly.
  width: string;
  num?: boolean;
}

function Table({ cols, children }: { cols: Col[]; children: ReactNode }) {
  // Zero margin/border: the CMS's global table rule would inset it inside the card.
  return (
    <table className={ST.table} style={{ borderCollapse: 'collapse', margin: 0, border: 0 }}>
      <thead
        className={`border-x-0 border-t-0 border-b border-line-subtle bg-surface-sunken ${ST.head}`}
      >
        <tr className="border-0">
          {cols.map((c) => (
            <th
              key={c.label}
              scope="col"
              className={`${TH} ${c.num ? '!text-right' : ''}`}
              style={{ width: c.width }}
            >
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={`${ST.body} max-md:py-1`}>{children}</tbody>
    </table>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="m-0 px-5 py-8 text-center text-[13px] text-content-muted">{text}</p>;
}

// Every row table leads with a Date column: one cell per IST day, spanning that day's rows.
const DATE_COL = (label: string): Col => ({ label, width: '13%' });
const L1_COLS: Col[] = [
  DATE_COL('Trial date'),
  { label: 'Student', width: '19%' },
  { label: 'Booked on', width: '15%' },
  { label: 'Trial (IST)', width: '10%' },
  { label: 'Status', width: '12%' },
  { label: 'Via', width: '13%' },
  { label: 'Now with', width: '18%' }
];
const L2_COLS: Col[] = [
  DATE_COL('Trial date'),
  { label: 'Student', width: '23%' },
  { label: 'Trial (IST)', width: '11%' },
  { label: 'Joined?', width: '14%' },
  { label: 'My touch before', width: '19%' },
  { label: 'Now with', width: '20%' }
];
// Joined / Paid on live in the Date column, so they have no column of their own.
const JOINED_COLS: Col[] = [
  DATE_COL('Joined'),
  { label: 'Student', width: '22%' },
  { label: 'Status', width: '19%' },
  { label: 'Last touch', width: '22%' },
  { label: 'Signal', width: '24%' }
];
// No "Credited to": the section is the rep's own cohort, so it would always read "You".
const CONVERTED_COLS: Col[] = [
  DATE_COL('Paid on'),
  { label: 'Student', width: '28%' },
  { label: 'Joined', width: '15%' },
  { label: 'Days to pay', width: '14%', num: true },
  { label: 'Plan', width: '30%' }
];

// Desktop: a rowSpan date cell on each day's first row. Phone (cards): a heading row per day.
function DateRows<T>({
  id,
  rows,
  getIso,
  noun,
  verb,
  now,
  rowKey,
  cells
}: {
  id: SectionId;
  rows: T[];
  getIso: (r: T) => string | null | undefined;
  noun: GroupNoun;
  verb?: HeadingVerb;
  now: number;
  rowKey: (r: T) => string;
  cells: (r: T) => ReactNode;
}) {
  return (
    <>
      {groupRowsByIstDate(rows, getIso).map((g, gi) => {
        const heading = dayHeading(g.day, now);
        const count = groupCountLabel(g.rows.length, noun);
        const key = `${id}-${g.day ?? 'none'}`;
        return (
          <React.Fragment key={key}>
            <tr
              data-testid="leads-date-heading"
              className={`hidden border-0 ${ST.row} max-md:!my-0`}
            >
              <td className="block px-3 pb-0.5 pt-3 text-left text-[13px] text-content-primary">
                <span className="font-bold">{phoneDayHeading(heading, verb)}</span>
                <span className="text-xs text-content-muted"> · {count}</span>
              </td>
            </tr>
            {g.rows.map((r, ri) => (
              <tr
                key={rowKey(r)}
                className={`${ROW} ${ri === 0 && gi > 0 ? 'md:border-t-line' : ''}`}
              >
                {ri === 0 ? (
                  <td
                    rowSpan={g.rows.length}
                    data-testid="leads-date-cell"
                    className="bg-surface-card px-3.5 py-2.5 text-left align-top max-md:hidden md:border-r md:border-line-subtle md:pl-5"
                  >
                    <span className="block text-[13px] font-bold leading-5 text-content-primary">
                      {heading}
                    </span>
                    <span className="block text-xs tabular-nums text-content-muted">{count}</span>
                  </td>
                ) : null}
                {cells(r)}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
    </>
  );
}

function SectionBody({ id, data, now }: { id: SectionId; data: MyLeadsData; now: number }) {
  const meta = sectionMeta(id);
  const count = sectionCount(data, id);
  const wrap = (rows: number, table: ReactNode) => (
    <Panel
      testId={`leads-section-${id}`}
      title={meta.title}
      sub={meta.sub}
      aside={count}
      asideTestId={`leads-count-${id}`}
    >
      {rows ? table : <Empty text={meta.empty} />}
    </Panel>
  );

  if (id === 'l1_trials') {
    const rows = data.l1?.rows || [];
    return wrap(
      rows.length,
      <Table cols={L1_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.start_at}
          noun="trial"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.start_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Booked on" className={`${TD} ${NUM}`}>
                {fmtDayTime(r.booked_at)}
              </StackTd>
              <StackTd label="Trial (IST)" className={`${TD} ${NUM}`}>
                {fmtTime(r.start_at) || '—'}
              </StackTd>
              <StackTd label="Status" className={TD}>
                <ToneText cell={trialStatusCell(r.status, 'L1')} />
              </StackTd>
              <StackTd label="Via" className={TD}>
                {viaLabel(r.via)}
              </StackTd>
              <StackTd label="Now with" className={`${TD} text-content-secondary`}>
                {nowWithLabel(r.now_with)}
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  if (id === 'l2_trials') {
    const rows = data.l2?.rows || [];
    return wrap(
      rows.length,
      <Table cols={L2_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.start_at}
          noun="trial"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.start_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Trial (IST)" className={`${TD} ${NUM}`}>
                {fmtTime(r.start_at) || '—'}
              </StackTd>
              <StackTd label="Joined?" className={TD}>
                <ToneText cell={trialStatusCell(r.status, 'L2')} />
              </StackTd>
              <StackTd label="My touch before" className={TD}>
                <ToneText cell={touchBeforeLabel(r.my_touch_before, r.start_at)} />
              </StackTd>
              <StackTd label="Now with" className={`${TD} text-content-secondary`}>
                {nowWithLabel(r.now_with)}
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  if (id === 'leads_joined') {
    const rows = data.leads_joined?.rows || [];
    return wrap(
      rows.length,
      <Table cols={JOINED_COLS}>
        <DateRows
          id={id}
          rows={rows}
          getIso={(r) => r.joined_at}
          noun="lead"
          verb="joined"
          now={now}
          rowKey={(r) => `${r.student_uuid}-${r.joined_at}`}
          cells={(r) => (
            <>
              <StackTd className={TD}>
                <StudentLink uuid={r.student_uuid} name={r.student_name} />
              </StackTd>
              <StackTd label="Status" className={TD}>
                <ToneText cell={leadStatusCell(r)} />
              </StackTd>
              <StackTd label="Last touch" className={TD}>
                <ToneText cell={lastTouchCell(r.last_touch, r.joined_at, now)} />
              </StackTd>
              <StackTd label="Signal" className={TD}>
                <ToneText cell={signalCell(r.signal, now)} />
              </StackTd>
            </>
          )}
        />
      </Table>
    );
  }

  const rows = data.converted?.rows || [];
  return wrap(
    rows.length,
    <Table cols={CONVERTED_COLS}>
      <DateRows
        id={id}
        rows={rows}
        getIso={(r) => r.paid_at}
        noun="member"
        verb="paid"
        now={now}
        rowKey={(r) => `${r.student_uuid}-${r.paid_at}`}
        cells={(r) => (
          <>
            <StackTd className={TD}>
              <StudentLink uuid={r.student_uuid} name={r.student_name} />
            </StackTd>
            <StackTd label="Joined" className={`${TD} ${NUM}`}>
              {r.joined_at ? fmtDay(r.joined_at) : <span className="text-content-muted">—</span>}
            </StackTd>
            <StackTd label="Days to pay" className={`${TD} ${NUM} md:text-right`}>
              <ToneText cell={daysToPayCell(r.days_to_pay)} />
            </StackTd>
            <StackTd label="Plan" className={TD}>
              {planLabel(r.plan_weeks, r.plan_label)}
            </StackTd>
          </>
        )}
      />
    </Table>
  );
}

const BAR_PX = 72;
const BAR_W = 12;
const CAME_IN_BG = 'var(--border-emphasis)';

// Swatch + label legend item; shared by the hour chart legend.
function Key({ swatch, children }: { swatch: ReactNode; children: ReactNode }) {
  return (
    <li className="inline-flex items-center gap-1.5">
      {swatch}
      {children}
    </li>
  );
}

const square = (background: string, opacity = 1) => (
  <span
    aria-hidden="true"
    className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm"
    style={{ background, opacity }}
  />
);

function HourChart({ v }: { v: ReturnType<typeof shiftView> }) {
  const { hours, showBars } = v;
  // A quiet shift shows the note; past-due counts stay only when some hour has one.
  const bubbles = hours.some((h) => !!h.pastDue);
  if (!showBars && !bubbles) {
    return (
      <span data-testid="shift-chart-note" className="text-xs text-content-muted">
        {v.chartNote}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
        <span className="text-xs font-semibold leading-5 text-content-secondary">By hour</span>
        <ul className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-content-muted">
          {showBars ? (
            <>
              <Key swatch={square(CAME_IN_BG)}>Came in</Key>
              <Key swatch={square('var(--state-success)')}>Done by me</Key>
              <Key swatch={square('var(--state-success)', 0.45)}>Done by others</Key>
            </>
          ) : null}
          <Key
            swatch={
              <span
                aria-hidden="true"
                className="inline-block h-2.5 w-2.5 shrink-0 rounded-full bg-state-error"
              />
            }
          >
            Past due at end of hour
          </Key>
        </ul>
      </div>
      {showBars ? null : (
        <span data-testid="shift-chart-note" className="text-xs text-content-muted">
          {v.chartNote}
        </span>
      )}
      {/* Phones: one row per hour, so no count hides behind a sideways scroll. */}
      <ul
        data-testid="shift-hour-list"
        className="m-0 flex list-none flex-col gap-1.5 p-0 md:hidden"
      >
        {hours.map((h) => {
          const t = hourRowText(h, showBars);
          return (
            <li
              key={h.label}
              data-testid={`shift-hour-row-${h.label}`}
              className="grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-x-2.5 text-xs tabular-nums"
            >
              <span
                className={h.current ? 'font-bold text-content-link' : 'text-content-secondary'}
              >
                {h.label}
              </span>
              <span className="flex min-w-0 flex-col gap-1">
                {t.counts ? <span className="text-content-primary">{t.counts}</span> : null}
                {showBars ? (
                  <span aria-hidden="true" className="flex flex-col gap-[2px]">
                    <span
                      className="block h-1.5 rounded-full"
                      style={{
                        width: `${h.inPct}%`,
                        background: CAME_IN_BG,
                        opacity: h.future ? 0.4 : 1
                      }}
                    />
                    <span
                      className="flex h-1.5 overflow-hidden rounded-full"
                      style={{ width: `${h.donePct}%` }}
                    >
                      <span
                        className="block h-full"
                        style={{ flex: h.doneMe, background: 'var(--state-success)' }}
                      />
                      <span
                        className="block h-full"
                        style={{
                          flex: h.doneOthers,
                          background: 'var(--state-success)',
                          opacity: 0.45
                        }}
                      />
                    </span>
                  </span>
                ) : null}
              </span>
              {t.pastDue ? (
                <span className="rounded-full bg-state-error-bg px-2 text-[11px] font-semibold leading-[18px] text-state-error-text">
                  {t.pastDue}
                </span>
              ) : (
                <span />
              )}
            </li>
          );
        })}
      </ul>
      {/* md+: bar chart; long or overnight shifts scroll inside the card, never the page. */}
      <div className="overflow-x-auto max-md:hidden">
        <div
          className="grid items-end"
          style={{
            gridTemplateColumns: `repeat(${hours.length}, minmax(48px, 1fr))`,
            minWidth: hours.length * 56
          }}
        >
          {hours.map((h) => (
            <div
              key={h.label}
              data-testid={`shift-hour-${h.label}`}
              role="img"
              aria-label={`${h.label}: ${h.cameIn} came in, ${h.doneMe} done by me, ${h.doneOthers} done by others${
                h.pastDue === null ? '' : `, ${h.pastDue} past due`
              }`}
              title={`${h.label}: ${h.cameIn} came in, ${h.doneMe + h.doneOthers} done${
                h.pastDue ? `, ${h.pastDue} past due` : ''
              }`}
              className="flex flex-col items-center justify-end px-1"
            >
              {/* Own row above the bars so the count never touches them. */}
              <span className="mb-2 flex h-[18px] items-center">
                {h.pastDue ? (
                  <span className="min-w-[20px] rounded-full bg-state-error-bg px-1.5 text-center text-[11px] font-semibold leading-[18px] tabular-nums text-state-error-text">
                    {h.pastDue}
                  </span>
                ) : null}
              </span>
              {showBars ? (
                <div
                  className="-mx-1 flex items-end justify-center gap-[3px] self-stretch border-b border-line-subtle"
                  style={{ height: BAR_PX }}
                >
                  <span
                    data-testid={`shift-bar-in-${h.label}`}
                    className="block rounded-t-[3px]"
                    style={{
                      width: BAR_W,
                      height: (h.inPct / 100) * BAR_PX,
                      background: CAME_IN_BG,
                      // Hour still running: lighter, not a different colour (sunken is near-black in dark).
                      opacity: h.future ? 0.4 : 1
                    }}
                  />
                  <span
                    data-testid={`shift-bar-done-${h.label}`}
                    className="flex flex-col justify-end overflow-hidden rounded-t-[3px]"
                    style={{ width: BAR_W, height: (h.donePct / 100) * BAR_PX }}
                  >
                    <span
                      className="block w-full"
                      style={{
                        flex: h.doneOthers,
                        background: 'var(--state-success)',
                        opacity: 0.45
                      }}
                    />
                    <span
                      className="block w-full"
                      style={{ flex: h.doneMe, background: 'var(--state-success)' }}
                    />
                  </span>
                </div>
              ) : null}
              <span
                className={`mt-1.5 text-[11px] tabular-nums ${
                  h.current ? 'font-bold text-content-link' : 'text-content-secondary'
                }`}
              >
                {h.label}
              </span>
              {showBars ? (
                <span className="text-center text-[11px] leading-4 tabular-nums text-content-muted">
                  {h.cameIn} in
                  <br />
                  {h.doneMe + h.doneOthers} done
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// One KPI tile look for the page tiles and the shift stats.
const STAT_LABEL = 'text-xs text-content-muted';
const STAT_VALUE = 'text-2xl font-semibold leading-8 tabular-nums text-content-primary';
const STAT_SUB = 'text-xs text-content-muted';

function ShiftCard({ data, now }: { data: MyLeadsData; now: number }) {
  const v = shiftView(data.shift, data.rep.level as RepLevel, now);
  return (
    <Panel
      testId="leads-shift"
      title="My shift today"
      sub={v.sub || undefined}
      aside={
        <>
          Work these from the <b className="font-semibold text-content-primary">Tasks</b> tab
        </>
      }
    >
      {v.mode === 'message' ? (
        <Empty text={v.message} />
      ) : (
        <div className="flex flex-col gap-5 px-4 py-5 sm:px-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {v.stats.map((s) => (
              <div
                key={s.key}
                data-testid={`shift-stat-${s.key}`}
                className="flex min-w-0 flex-col gap-1 rounded-[10px] border border-line-subtle px-4 py-3"
              >
                <span className={`inline-flex items-center gap-1.5 ${STAT_LABEL}`}>
                  <span
                    aria-hidden="true"
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ background: s.color }}
                  />
                  {s.label}
                </span>
                <span className={STAT_VALUE}>{s.value}</span>
                <span className={STAT_SUB}>{s.detail || '\u00a0'}</span>
              </div>
            ))}
          </div>
          {v.backlog ? (
            <div
              data-testid="shift-backlog"
              className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-surface-sunken px-3 py-2 text-xs text-content-secondary"
            >
              <span>{v.backlog}</span>
              <Link to="/sales-task-list?tab=tasks" className="font-semibold text-content-link">
                Open Tasks
              </Link>
            </div>
          ) : null}
          <div className="flex flex-col gap-3">
            <div
              role="img"
              aria-label={v.aria}
              className="flex h-3 overflow-hidden rounded-full bg-surface-sunken"
            >
              {v.segments
                .filter((g) => g.value > 0)
                .map((g) => (
                  <span
                    key={g.key}
                    title={`${g.label}: ${g.value}`}
                    className="block h-full"
                    style={{ width: `${g.pct}%`, background: g.color, opacity: g.opacity ?? 1 }}
                  />
                ))}
            </div>
            <ul className="m-0 grid max-w-[760px] list-none grid-cols-1 gap-x-8 gap-y-1.5 p-0 text-xs text-content-secondary sm:grid-cols-2 lg:grid-cols-3">
              {v.segments.map((g) => (
                <li key={g.key} className="flex items-center gap-2">
                  {square(g.color, g.opacity ?? 1)}
                  <span className="min-w-0 flex-1">{g.label}</span>
                  <b className="font-semibold tabular-nums text-content-primary">{g.value}</b>
                </li>
              ))}
            </ul>
          </div>
          {v.hours.length ? (
            <div className="border-t border-line-subtle pt-5">
              <HourChart v={v} />
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

interface ViewProps {
  data: MyLeadsData;
  // Reference time for "ago" labels and the current shift hour; fixed per response.
  now: number;
  period: LeadsPeriod;
  onPeriod: (p: LeadsPeriod) => void;
  // Team's Leads view: a manager looking at someone else's page.
  viewing?: boolean;
  loading?: boolean;
  // The shown numbers are for another period while the new one loads.
  stale?: boolean;
  banner?: ReactNode;
  // Team's Leads rep dropdown, shown in place of the page title.
  picker?: ReactNode;
}

// Team's Leads card holding only the rep dropdown: nobody picked yet, or the picked page has no view.
export function PickerBar({ children }: { children: ReactNode }) {
  return (
    <section
      aria-label="Pick a team member"
      data-testid="team-leads-toolbar"
      className={`mx-[5%] mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 px-3.5 py-3 max-sm:mx-3 max-sm:flex-col max-sm:items-stretch ${CARD}`}
    >
      {children}
    </section>
  );
}

function MyLeadsView({
  data,
  now,
  period,
  onPeriod,
  viewing,
  loading,
  stale,
  banner,
  picker
}: ViewProps) {
  const level = data.rep.level as RepLevel;
  const tiles = tilesFor(data);
  const updated = updatedLabel(data.generated_at);
  const dueAt = refreshDueAt(data.generated_at, data.next_refresh_at, now, data.server_time);
  const chip = (
    <Chip tone={LEVEL_TONE[level]} testId="leads-level-chip">
      {level} view
    </Chip>
  );
  return (
    <div
      className="mx-[5%] my-5 flex flex-col gap-3.5 text-[13px] text-content-primary max-sm:mx-3"
      data-testid="my-leads-view"
    >
      {/* Summary bar, as on Session allocation: title left, controls and refresh state right. */}
      <section
        aria-label="Leads summary"
        data-testid="leads-header"
        className={`flex flex-wrap items-center gap-x-4 gap-y-2.5 px-3.5 py-3 max-sm:flex-col max-sm:items-stretch ${CARD}`}
      >
        <div className={`flex min-w-0 flex-col ${picker ? 'gap-1.5' : 'gap-0.5'}`}>
          {picker ? (
            // Team's Leads: the rep dropdown stands in for the title; it already shows the name.
            <div className="flex min-w-0 items-center gap-2">
              {picker}
              <span className="shrink-0">{chip}</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="m-0 text-[15px] font-bold leading-5 text-content-primary">
                {viewing ? `${data.rep.name}'s leads` : 'My Leads'}
              </h2>
              {chip}
            </div>
          )}
          <span className="text-xs text-content-secondary">
            {viewing
              ? `The same page ${data.rep.name} sees as My Leads.`
              : 'Only your own students. Your manager sees the same page for any rep.'}
          </span>
        </div>
        <span className="flex-1 max-sm:hidden" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 max-sm:flex-col max-sm:items-stretch">
          <LeadsSelect
            id="my-leads-period"
            label="Period"
            value={String(period)}
            options={PERIODS.map((p) => ({ value: String(p), label: periodLabel(p) }))}
            onChange={(v) => onPeriod(Number(v) as LeadsPeriod)}
            active={period !== DEFAULT_PERIOD}
          />
          <span
            className="flex flex-wrap gap-x-1.5 text-[11.5px] tabular-nums text-content-muted sm:flex-col"
            aria-live="off"
            data-testid="leads-refresh"
          >
            <RefreshCountdown dueAt={dueAt} loading={loading} />
            {updated ? (
              <span>
                <span aria-hidden="true" className="sm:hidden">
                  ·{' '}
                </span>
                {updated}
              </span>
            ) : null}
          </span>
        </div>
      </section>

      {banner}

      <div
        className="flex flex-col gap-3.5 transition-opacity"
        aria-busy={stale || undefined}
        style={stale ? { opacity: 0.5 } : undefined}
        data-testid="leads-body"
      >
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {tiles.map((t) => (
            <div
              key={t.key}
              data-testid={`leads-tile-${t.key}`}
              className={`flex min-w-0 flex-col gap-1 px-4 py-3 ${CARD}`}
              style={t.success ? { background: 'var(--brand-primary-soft)' } : undefined}
            >
              <span className={STAT_LABEL}>{t.label}</span>
              <span className={STAT_VALUE}>{t.value}</span>
              {t.hint ? <span className={STAT_SUB}>{t.hint}</span> : null}
            </div>
          ))}
        </div>

        <ShiftCard data={data} now={now} />

        {sectionsFor(level).map((id) => (
          <SectionBody key={id} id={id} data={data} now={now} />
        ))}
      </div>

      <Panel testId="leads-help" title="How these numbers work">
        <ul className="m-0 list-disc py-4 pl-9 pr-5 text-xs leading-[1.5] text-content-secondary">
          {helpBullets(level, data.period_start_ist, data.period_end_ist).map((b) => (
            <li key={b} className="py-0.5">
              {b}
            </li>
          ))}
        </ul>
      </Panel>
    </div>
  );
}

// Memoised so the countdown tick and unrelated page state do not re-render the tables.
const MemoMyLeadsView = memo(MyLeadsView);

export default MemoMyLeadsView;
